'use client';

import { FC, useState } from 'react';
import {
  Check, X, ArrowRight, Play, ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Announcement } from '@/services/announcements';
import { getAnnouncementTypeConfig } from './typeConfig';
import AnnouncementVideo from './AnnouncementVideo';

interface AnnouncementToastViewProps {
  announcement: Announcement;
  onDismiss?: () => void;
  onCta?: () => void;
}

/** Bottom-right floating toast card. Self-positioning is handled by the container. */
const AnnouncementToastView: FC<AnnouncementToastViewProps> = ({ announcement, onDismiss, onCta }) => {
  const c = getAnnouncementTypeConfig(announcement.type);
  const spotlight = announcement.style === 'spotlight';
  const Icon = c.icon;
  const bullets = (announcement.bullets ?? []).filter((b) => b && b.trim());
  const isVideo = announcement.media_type === 'video';
  const inlineVideo = isVideo && announcement.video_mode === 'inline' && !!announcement.video_url;
  const [playing, setPlaying] = useState(false);
  const handleCta = () => {
    if (inlineVideo) setPlaying((p) => !p);
    else onCta?.();
  };
  const ctaLabel = inlineVideo && playing ? 'Hide video' : announcement.cta_label;

  return (
    <div
      className={cn(
        'relative w-[372px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-2xl shadow-xl',
        spotlight ? 'border border-[#06205f] bg-[#04216b]' : 'border border-slate-200 bg-white',
      )}
    >
      {spotlight && (
        <span className="pointer-events-none absolute -top-16 right-[-40px] h-52 w-60 rounded-full bg-emerald-400 opacity-45 blur-[120px]" />
      )}
      <div className="relative p-[18px]">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
              spotlight ? 'border-white/20 bg-white/10 text-emerald-300' : cn('bg-white', c.tintBorder, c.accentText),
            )}
          >
            <Icon className="h-[18px] w-[18px]" strokeWidth={2.1} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <span className={cn('text-[10.5px] font-bold uppercase tracking-wider', spotlight ? 'text-emerald-300' : c.accentText)}>
                {c.label}
              </span>
              {announcement.dismissible && onDismiss && (
                <button
                  type="button"
                  aria-label="Dismiss"
                  onClick={onDismiss}
                  className={cn(
                    'flex h-[26px] w-[26px] items-center justify-center rounded-lg transition-colors',
                    spotlight ? 'text-white/70 hover:bg-white/10' : 'text-slate-400 hover:bg-slate-100',
                  )}
                >
                  <X className="h-4 w-4" strokeWidth={2.2} />
                </button>
              )}
            </div>
            <div className={cn('mt-0.5 text-[15px] font-semibold tracking-tight', spotlight ? 'text-white' : 'text-slate-900')}>
              {announcement.title}
            </div>
            {announcement.body && (
              <div className={cn('mt-1 text-[13px] leading-relaxed', spotlight ? 'text-white/70' : 'text-slate-600')}>
                {announcement.body}
              </div>
            )}
            {bullets.length > 0 && (
              <ul className="mt-2.5 flex flex-col gap-1.5 p-0">
                {bullets.map((it, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <Check className={cn('mt-0.5 h-3.5 w-3.5 shrink-0', spotlight ? 'text-emerald-300' : c.accentText)} strokeWidth={2.6} />
                    <span className={cn('text-[13px] leading-snug', spotlight ? 'text-white/80' : 'text-slate-700')}>{it}</span>
                  </li>
                ))}
              </ul>
            )}
            {inlineVideo && playing && (
              <div className="mt-3">
                <AnnouncementVideo videoUrl={announcement.video_url as string} posterUrl={announcement.poster_url} />
              </div>
            )}
            {announcement.cta_label && (
              <div className="mt-3.5">
                <button
                  type="button"
                  onClick={handleCta}
                  className={cn(
                    'inline-flex items-center gap-1.5 whitespace-nowrap rounded-[10px] px-3.5 py-2 text-[13px] font-semibold transition-colors',
                    spotlight ? 'bg-white text-blue-800 hover:bg-emerald-300' : cn('text-white', c.ctaBg),
                  )}
                >
                  {isVideo && !(inlineVideo && playing) && <Play className="h-3.5 w-3.5" strokeWidth={2.4} />}
                  {ctaLabel}
                  {inlineVideo && playing
                    ? <ChevronUp className="h-3.5 w-3.5" strokeWidth={2.4} />
                    : !isVideo && <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementToastView;
